import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import doorState from 'config/doorState';
import unloadSpeed from 'config/unloadSpeed';

import { Row, Col } from 'components/layout';
import { Text, Link } from 'components/Text';
import Icon from 'components/Icon';

const Board = ({ zone, doors, alerts, ...rest }) => {
  const doorsInOneRow = 10;

  const splits = useMemo(() => {
    if (!doors.length) {
      return [];
    }

    const sortedDoors = [...doors].sort((a, b) => a.display_order - b.display_order);
    const startRow = Math.floor(sortedDoors[0].display_order / doorsInOneRow);
    const endRow = Math.floor(sortedDoors[doors.length - 1].display_order / doorsInOneRow);
    const splits = new Array(endRow + 1).fill([]).map(() => {
      return new Array(doorsInOneRow).fill(null);
    });

    return doors.reduce((splits, door) => {
      const rowIndex = Math.floor(door.display_order / doorsInOneRow) - startRow;
      const colIndex = door.display_order % doorsInOneRow;
      splits[rowIndex][colIndex] = door;
      return splits;
    }, splits);
  }, [doors]);

  return (
    <StyledBoard {...rest}>
      <Row style={{ marginBottom: '1vh' }}>
        <Col flex={1} style={{ width: '7%', position: 'relative' }}>
          <ZoneName>{zone}</ZoneName>
        </Col>
      </Row>
      {
        splits.map((split, splitIndex) => (
          <Row key={splitIndex} style={{ flexWrap: 'nowrap' }}>
            {
              split.map((door, doorIndex) => {
                if (!door) {
                  return (
                    <Col key={splitIndex * doorsInOneRow + doorIndex} style={{ flexBasis: `${100 / doorsInOneRow}%` }}>
                      {/* <DoorPlaceholder /> */}
                    </Col>
                  );
                }

                const hasAlerts = alerts.includes(door.door_id);

                return (
                  <Col key={door.display_order} style={{ flexBasis: `${100 / doorsInOneRow}%` }}>
                    <Link target="_blank" to={`/doors/${door.door_id}`}>
                      <Door door={door} hasAlerts={hasAlerts} />
                    </Link>
                  </Col>
                );
              })
            }
          </Row>
        ))
      }
    </StyledBoard>
  );
};

const Door = React.memo(({ door, hasAlerts }) => {
  if (!door) return null;

  const NO_CAMERA_COLOR = '#EFF2F5';
  const WHITE_COLOR = '#FFFFFF';

  const color = doorState.getColor(door.door_state) || unloadSpeed.getColor(door.relative_speed);
  // const isNotStarted = door.door_state === doorState.NOT_STARTED;
  
  const maintenanceMode = door.maintenance_mode === 1;
  const hasCamera = door.has_cam === 1 && !maintenanceMode;
  const direction = door.direction;
  const isLoad = direction === 'LOAD';
  const isFavorite = door.is_favorite === 1;
  const hasAnomaly = hasCamera && door.is_low_accuracy === 1;
  const pulse = hasCamera && hasAlerts;
  const unidentifiedEmployees = door.has_missing_tags || 0;

  const isStorageEquipment = door.container_code === "Equipment used for Pallets" || door.container_code === "Equipment used for Storage";
  let bgColor = color, borderColor = color, textColor = WHITE_COLOR;
  if (!hasCamera || isStorageEquipment) {
    bgColor = NO_CAMERA_COLOR;
    borderColor = NO_CAMERA_COLOR;
    textColor = '#232526';
  } else if (door.door_state !== doorState.EMPTY && isLoad) {
    bgColor = 'transparent';
    textColor = color;
  }
  const hasTriangle = maintenanceMode || hasAnomaly;
  const showCameraOff = !hasCamera && !hasTriangle;
  const showArchive = isStorageEquipment && !hasTriangle && !showCameraOff;

  return (
    <DoorWrapper
      pulse={pulse}
      bgColor={bgColor}
      borderColor={borderColor}
    >
      <DoorName
        alternate={unidentifiedEmployees}
        style={{
          color: textColor,
        }}
      >
        {door.display_name}
      </DoorName>
      <UnidentifiedPerson
        alternate={unidentifiedEmployees}
      >
        <Icon
          name="user"
          size={'2.4vh'}
          color={hasCamera ? '#ffffff' : '#232526'}
          strokeWidth={2}
        />
      </UnidentifiedPerson>
      {isFavorite && <Icon
        name="star"
        size={'1.5vh'}
        fill="#FFF566"
        color={isLoad ? "#232526" : "#FFF566"}
        style={{ position: 'absolute', left: '0.8vh', top: '0.4vh' }}
      />}
      {showCameraOff && (
        <Icon
          name={'video-off'}
          size={'1.6vh'}
          color="#000000"
          strokeWidth={3}
          style={{ position: 'absolute', right: '1.6vh', top: '0.4vh' }}
        />
      )}
      {hasTriangle && (
        <Icon
          name={'alert-triangle'}
          size="1.6vh"
          color={maintenanceMode ? '#000000' : (hasCamera ? (isLoad ? "#232526" : "#ffffff") : "#232526")}
          strokeWidth={3}
          style={{ position: 'absolute', right: '1.6vh', bottom: '1.2vh' }}
        />
      )}
      {showArchive && (
        <Icon
          name="archive"
          size={'1.6vh'}
          color={hasCamera ? '#000000' : '#232526'}
          strokeWidth={3}
          style={{ position: 'absolute', left: '0.8vh', bottom: '0.8vh' }}
        />
      )}
      {/* {door.door_state !== doorState.EMPTY && hasCamera && <Icon
        name={{ LOAD: 'arrow-up', UNLOAD: 'arrow-down' }[direction]}
        size="1.6vh"
        color="#ffffff"
        strokeWidth={3}
        style={{ position: 'absolute', left: '0.8vh', bottom: '1.1vh' }}
      />} */}
    </DoorWrapper>
  );

}, (prevProps, nextProps) => {
  if (prevProps.hasAlerts !== nextProps.hasAlerts) {
    return false;
  }

  return [
    'door_state',
    'has_cam',
    'is_favorite',
    'is_low_accuracy',
    'container_code',
    'has_missing_tags',
    'direction',
    'relative_speed',
  ].every(field => {
    if (!prevProps.door || !nextProps.door) {
      return false;
    }
    return prevProps.door[field] === nextProps.door[field];
  });
});

// const DoorPlaceholder = () => {
//   const noCameraColor = '#EFF2F5';

//   return (
//     <DoorWrapper bgColor={noCameraColor} />
//   );
// };

const StyledBoard = styled.div`
  display: block;
`;

const DoorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 2.4vh;
  line-height: 2.7vh;
  margin-right: 0.6vh;
  margin-bottom: 0.6vh;

  cursor: pointer;
  height: 4.6vh;
  border-radius: 0.6vh;
  background-color: ${props => props.bgColor};
  border: 0.4vh solid ${props => props.borderColor};

  ${props => props.pulse ? css`
    animation: ${pulseKeyframe} 0.6s ease infinite;
  ` : null}
`;

const DoorName = styled(Text)`
  ${props => props.alternate ? css`
    animation: ${fadeOutKeyframe};
    animation-duration: 6.25s;
    animation-fill-mode: both;
    animation-iteration-count: infinite;
    animation-direction: alternate-reverse;
    animation-timing-function: linear;
  ` : null}
`;

const UnidentifiedPerson = styled.div`
  position: absolute;
  inset: 0px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0;

  ${props => props.alternate ? css`
    animation: ${fadeInKeyframe};
    animation-duration: 6.25s;
    animation-fill-mode: both;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    animation-timing-function: linear;
  ` : null}
`;

const ZoneName = styled(Text)`
  display: inline-block;
  // position: absolute;
  font-weight: 600;
  font-size: 1.62vw;
  line-height: 1.2;
  color: #000;
  // transform: rotate(-90deg) translateX(-40%);
`;

const pulseKeyframe = keyframes`
  0%, 100% {
    transform: scale(1.0)
  }
  50% {
    transform: scale(1.1)
  }
`;

const fadeInKeyframe = keyframes`
  0%, 40% {
    opacity: 0;
    // transform: translateY(-50%);
  }

  60%, 100% {
    opacity: 1;
    // transform: translateY(0%);
  }
`;

const fadeOutKeyframe = keyframes`
  0%, 40% {
    opacity: 0;
    // transform: translateY(50%);
  }

  60%, 100% {
    opacity: 1;
    // transform: translateY(0%);
  }
`;


export default Board;